import * as Sentry from '@sentry/react';

import { createTheme, DeprecatedThemeOptions } from '@mui/material/styles';
import { TypographyOptions } from '@mui/material/styles/createTypography';

export const REACT_APP_ENV = Object.keys(process.env).includes('REACT_APP_ENV')
    ? process.env.REACT_APP_ENV
    : 'staging';

export let SAMARITAN_URL = '';

const SAMARITAN_DOMAIN = process.env.REACT_APP_SAMARITAN_DOMAIN;
const SAMARITAN_DEV_URL = process.env.REACT_APP_SAMARITAN_DEV_URL;
if (SAMARITAN_DEV_URL) {
    SAMARITAN_URL = SAMARITAN_DEV_URL;
} else if (SAMARITAN_DOMAIN) {
    SAMARITAN_URL = `https://www.${SAMARITAN_DOMAIN}`;
} else if (process.env.NODE_ENV === 'development') {
    SAMARITAN_URL = 'https://staging.samaritan.city';
} else {
    SAMARITAN_URL = 'https://www.samaritan.city';
}

export const CHECKIN_CYCLE_DAYS = 30;
export const GOAL_MATCHING_AMOUNT = 10;

/**
 * Get password reset URL based on current environment
 */
export const getPasswordResetUrl = () => {
    return `${SAMARITAN_URL}/users/password/new`;
};

const samaritanFontFamilies = [
    'Manrope',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"'
].join(',');

interface CustomTypographyOptions extends TypographyOptions {
    fontWeight: string | number;
}

interface CustomThemeOptions extends DeprecatedThemeOptions {
    typography: CustomTypographyOptions;
    colors: {
        [key: string]: string;
    };
}

export const SAMARITAN_MATERIAL_UI_THEME = createTheme({
    typography: {
        fontFamily: samaritanFontFamilies,
        fontWeight: 300
    },
    colors: {
        White: 'white',

        Beige01: '#fcfaf7',
        OffWhite01: '#fbfbff',

        Purple01: '#7378e8',
        Purple02: '#b5b8eb',
        Purple03: '#a19ed6',
        Purple04: '#cacceb',
        Purple05: '#7378e815',
        Purple06: '#7378e806',
        Purple07: '#7279e8',

        DarkPurple01: '#1c1c47',
        DarkPurple02: '#1f2152',
        DarkPurple03: '#595985',
        DarkPurple04: '#3d3d6e',
        DarkPurple05: '#33305c',
        DarkPurple06: '#2b2e69',

        LightPurple01: '#f0f0ff',
        LightPurple02: '#d9d9ff',
        LightPurple03: '#ededfa',
        LightPurple04: '#f2f2fd',

        Grey01: '#9494a8',

        DarkGrey01: '#363636',

        LightGrey01: '#9696ba',
        LightGrey02: '#a6a6bf',
        LightGrey03: '#e3e3fa',
        LightGrey04: '#e3e3fa6a',
        LightGrey05: '#e3e3e3',

        Red01: '#f5577a',
        LightRed01: '#F96C8B',

        Green01: '#7fe8b2',

        Facebook: '#4073e5'
    }
} as CustomThemeOptions);

const FeatureToggleSets = {
    development: {
        dashboardOverview: true,
        organizationalMetrics: true,
        clientSnapshot: true,
        lifeCareVisit: true
    },
    production: {
        dashboardOverview: false,
        organizationalMetrics: false,
        clientSnapshot: false,
        lifeCareVisit: false
    },
    test: {
        dashboardOverview: false,
        organizationalMetrics: false,
        clientSnapshot: false,
        lifeCareVisit: false
    }
};

/**
 * Either use process.env.NODE_ENV or process.env.REACT_APP_FEATURE_TOGGLE_SET
 * to select the set of toggles to use
 */
export const FeatureToggles =
    FeatureToggleSets[
        (process.env.REACT_APP_TOGGLE_SET as keyof typeof FeatureToggleSets) ||
            (process.env.NODE_ENV as keyof typeof FeatureToggleSets)
    ];

/**
 * Set user for Sentry
 */
export function setSentryUser({ email, id }: { email: string; id: string }) {
    Sentry.setUser({ email, id });
}
